import { useQuery } from "@tanstack/react-query";
import { getSKUMovementEnquiry } from "services/data.report.service";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { AdjustmentColumns } from "./columns";
import { useState, useEffect } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { getAdjustmentEnquiry, exportAdjustmentAllCustomer } from "services/data.report.service";
import { CSVLink } from "react-csv";
import { useTeamValue } from "providers/TeamContext";
import dayjs from "dayjs";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import { Grid } from "@mui/material";
import { LoadingDialog, Success, Error, Error_ } from "ui-components/Alert";
import { ExportButton, EmailButton } from "ui-components/Button";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import useFileDownloadHook from "hooks/useFileDownloadHook";
import { ExportDialog } from "ui-components/Alert";
import { getCustomerOptions } from "services/data.internal.service";
import { SelectFields } from "ui-components/Form";

function AdjustmentEnquiry() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode, customerOpt } = useCustomerValue();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const exportPermission =
    allowAll ||
    (Boolean(permissions["Adjustment_Report"]) &&
      permissions["Adjustment_Report"].findIndex((b) => b === "export") !== -1);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const sevenDaysBefore = dayjs(new Date()).subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(sevenDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const [alert, setAlert] = useState(null);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const { downloadFile, isDownloading: isExporting, error: exportError } = useFileDownloadHook();
  const [customers, setCustomers] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const isAllCustomer = customers.includes("ALL CUSTOMER");

  useEffect(() => {
    if (exportError) {
      setAlert(<Error_ content={exportError.message} onChange={setAlert} />);
    }
  }, [exportError]);

  const validateDateRange = () => {
    setAlert(null);
    const valid =
      dayjs(endDate).isSame(dayjs(startDate)) || dayjs(endDate).isAfter(dayjs(startDate));
    if (!valid) {
      setAlert(<Error_ content="Start date cannot greater than end date" onChange={setAlert} />);
      return false;
    }
    if (dayjs(date).isBefore(endDate)) {
      setAlert(<Error_ content="End date cannot greater than today date" onChange={setAlert} />);
      return false;
    }
    return true;
  };

  const onFetch = () => {
    if (!validateDateRange()) return;
    refetch();
  };

  const onExportAllCustomer = () => {
    if (!validateDateRange()) return;
    const filename = `ALLCUSTOMER_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_Adjustment.xlsx`;
    downloadFile(() => exportAdjustmentAllCustomer({ startDate, endDate }), filename);
  };

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["adjustment-enquiry", { customers }],
    queryFn: () => getAdjustmentEnquiry({ customers, customerCode, startDate, endDate }),
    enabled: Boolean(customerCode) && Boolean(customers.length) && !isAllCustomer,
    onSuccess: (data) => {
      setData(data.data.data);
      setFilteredData(data.data.data);
    },
    onError: () => {},
  });

  useQuery({
    queryKey: ["customer-options", { customerCode }],
    queryFn: () => getCustomerOptions(),
    onSuccess: (data) => {
      const activeCust = customerOpt.filter((x) => data.data.some((y) => y === x));
      setCustomerOptions(activeCust);
      setCustomers([customerCode]);
    },
    onError: () => {},
  });

  const onSuccess = (data) => {
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => {
    setAlert(<Error data={data} onChange={setAlert} />);
  };
  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {(isDownloading || isExporting) && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}
      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem 0 0 1rem">
          <Grid container spacing={3}>
            <Grid item xs={5}>
              {Boolean(customerOptions) && Boolean(customerOptions.length) && (
                <SelectFields
                  field={{
                    name: "",
                    label: "Customers",
                    value: customers,
                    errors: {},
                    multiple: true,
                  }}
                  options={["ALL CUSTOMER", ...customerOptions]}
                  onChange={(e, v) => {
                    if (v.includes("ALL CUSTOMER")) {
                      setCustomers(["ALL CUSTOMER"]);
                      setData([]);
                      setFilteredData([]);
                    } else {
                      setCustomers(v);
                    }
                  }}
                />
              )}
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <CustomDateRangePicker {...customProps} />
            </Grid>
            <Grid item xs={3}>
              {isAllCustomer ? (
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    onExportAllCustomer();
                  }}
                >
                  export
                </MDButton>
              ) : (
                <>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => {
                      onFetch();
                    }}
                  >
                    search
                  </MDButton>
                  <MDButton
                    sx={{ marginLeft: "1rem" }}
                    variant="gradient"
                    color="info"
                    disabled={!exportPermission}
                    onClick={() => {
                      const filename = `${customerCode}_${dayjs(new Date()).format(
                        "YYYYMMDD-HHmm"
                      )}_Adjustment`;
                      downloadCsv(filename, filteredData);
                    }}
                  >
                    export
                  </MDButton>
                </>
              )}
            </Grid>
            {!isAllCustomer && (
              <Grid item xs={5}>
                <EmailButton
                  variant="gradient"
                  color="info"
                  filename={`${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_Adjustment`}
                  fileData={filteredData}
                  onSuccess={onSuccess}
                  onError={onError}
                  customerCode={customerCode}
                />
              </Grid>
            )}
          </Grid>
        </MDBox>
        {!isAllCustomer && (
          <BasicTable
            data={data}
            columns={AdjustmentColumns}
            onFilterRowsChange={handleFilterRowsChange}
          />
        )}
      </TableCard>
    </ListLayout>
  );
}

export default AdjustmentEnquiry;
